/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from 'reactstrap';
import { Alert, AlertDescription, Tag } from '@jem-open/jem-ui';
import moment from 'moment';
import axios from 'axios';
import { environment } from '../../../environment/environment';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { Pager } from '../../../components/pager';

const SmsList = () => {
  const [smsLogs, setSmsLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const latestRequestId = useRef(0);

  const getSmsLogs = useCallback((pageNumber) => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setIsLoading(true);
    setError('');
    axios
      .get(`${environment.baseUrl}/smslogs/`, {
        params: { page: pageNumber, pagination: true },
      })
      .then((res) => {
        if (requestId !== latestRequestId.current) return;
        setSmsLogs(res.data.results || res.data);
        setNumberOfPages(res.data.pages || 1);
        setCurrentPage(pageNumber);
        setIsLoading(false);
      })
      .catch((err) => {
        if (requestId !== latestRequestId.current) return;
        setError(err.message || 'Failed to load SMS logs');
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const INITIAL_PAGE_NUMBER = 1;
    getSmsLogs(INITIAL_PAGE_NUMBER);
  }, [getSmsLogs]);

  const columns = [
    {
      key: 'message',
      label: 'Message',
      className: 'max-w-[420px]',
      render: (value) => (
        <div className="w-full truncate" title={value || ''}>
          {value || '—'}
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
    },
    {
      key: 'successful_send',
      label: 'Successful',
      width: '120px',
      render: (value) => (
        <Tag variant={value ? 'success' : 'failed'}>{value ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      key: 'mobile_number',
      label: 'Mobile Number',
      width: '160px',
    },
    {
      key: 'date_created',
      label: 'Date Added',
      width: '230px',
      render: (value) => {
        const date = moment(value);
        return date.isValid() ? date.format('MMMM Do YYYY, h:mm:ss a') : '—';
      },
    },
  ];

  const emptyState = (
    <EmptyState icon="fas fa-sms" message="No SMS logs found" />
  );

  return (
    <div className="min-h-screen bg-white p-md">
      <div
        className="-mx-md -mt-md mb-md px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
        <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
          SMS Logs
        </h1>
        <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
          Review outbound SMS delivery activity and status.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-md">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="shadow border-0">
        <div className="table-responsive">
          <DataTable
            columns={columns}
            data={smsLogs}
            loading={isLoading}
            emptyState={emptyState}
            skeletonRows={8}
          />
        </div>
      </Card>

      {!isLoading && numberOfPages > 1 && (
        <div className="mt-md flex justify-center">
          <Pager
            currentPage={currentPage}
            totalPages={numberOfPages}
            onPageChange={getSmsLogs}
          />
        </div>
      )}
    </div>
  );
};

export default SmsList;
